import debounce from 'lodash-es/debounce.js';
import { _converse, api, u, RosterFilter } from '@converse/headless';
import { CustomElement } from 'shared/components/element.js';
import { __ } from 'i18n';
import tplMUCOccupants from './templates/muc-occupants.js';
import { destroyMUC } from './utils.js';
import 'shared/autocomplete/index.js';
import './modals/muc-invite.js';
import './occupant.js';

import 'shared/styles/status.scss';
import './styles/muc-occupants.scss';

const { initStorage } = u;

// How long a "delete all messages" action (local-only -- see startDeleteForMe
// / startDeleteForEveryone) stays undoable before the local store is
// actually cleared. Delayed on purpose so the toast's "Скасувати" is a real
// undo rather than cosmetic -- nothing is wiped until the grace period elapses.
const DELETE_UNDO_GRACE_MS = 5000;

export default class MUCOccupants extends CustomElement {
    constructor() {
        super();
        this.jid = null;
        // Transient UI-only state -- deliberately plain instance fields
        // (not model attributes) so none of it gets persisted to this MUC's
        // browserStorage; `this.requestUpdate()` is called manually
        // wherever these change instead of relying on a model 'change' event.
        this.group_menu_view = null; // null | 'more' | 'delete-confirm' | 'disappearing-timer'
        this.pending_delete = null; // { kind: 'me'|'everyone', count, timeout_id } | null
        // Viewport-relative {top, right} in px, captured once from the
        // "Більше" button when the menu first opens (see openMoreMenu) and
        // reused unchanged while navigating its sub-views, so the popup
        // doesn't jump to wherever the user happens to click *inside* it.
        this.menu_anchor = null;
    }

    static get properties() {
        return {
            jid: { type: String },
        };
    }

    initialize() {
        const filter_id = `_converse.occupants-filter-${this.jid}`;
        this.filter = new RosterFilter();
        this.filter.id = filter_id;
        initStorage(this.filter, filter_id);
        this.filter.fetch();

        const { chatboxes } = _converse.state;
        this.model = chatboxes.get(this.jid);

        // To avoid rendering continuously the participant list in case of massive joins/leaves:
        const debouncedRequestUpdate = debounce(() => this.requestUpdate(), 200, {
            maxWait: 1000,
        });

        this.listenTo(this.model, 'change', () => this.requestUpdate());
        this.listenTo(this.model.occupants, 'add', debouncedRequestUpdate);
        this.listenTo(this.model.occupants, 'change', debouncedRequestUpdate);
        this.listenTo(this.model.occupants, 'remove', debouncedRequestUpdate);
        this.listenTo(this.model.occupants, 'sort', debouncedRequestUpdate);
        this.listenTo(this.model.features, 'change:open', () => this.requestUpdate());

        this.model.initialized.then(() => this.requestUpdate());
    }

    render() {
        return tplMUCOccupants(this);
    }

    /**
     * @param {MouseEvent} ev
     */
    showInviteModal(ev) {
        ev.preventDefault();
        api.modal.show('converse-muc-invite-modal', { muc: this.model }, ev);
    }

    /** @param {MouseEvent} ev */
    toggleFilter(ev) {
        ev?.preventDefault?.();
        u.safeSave(this.model, { 'filter_visible': !this.model.get('filter_visible') });
    }

    /** @param {MouseEvent} ev */
    closeSidebar(ev) {
        ev?.preventDefault?.();
        u.safeSave(this.model, { 'hidden_occupants': true });
    }

    /** @param {MouseEvent} [ev] */
    togglePin(ev) {
        ev?.preventDefault?.();
        this.model.save('pinned', !this.model.get('pinned'));
    }

    /** @param {MouseEvent} [ev] */
    toggleMute(ev) {
        ev?.preventDefault?.();
        this.model.save('muted', !this.model.get('muted'));
    }

    /** @param {MouseEvent} [ev] */
    openMoreMenu(ev) {
        ev?.preventDefault?.();
        // `.occupants` (and other ancestors) has its own overflow/scroll
        // handling for the participant list, which clips or scrolls a
        // plain position:absolute popup instead of letting it float freely
        // above everything -- position:fixed with coordinates computed
        // here from the actual trigger button sidesteps that entirely,
        // the same way Bootstrap's Popper-based dropdowns (used for the
        // per-message actions menu) resolve position at runtime rather
        // than via a fixed CSS offset.
        const btn = ev?.currentTarget;
        if (btn) {
            const rect = btn.getBoundingClientRect();
            this.menu_anchor = { top: rect.bottom, right: window.innerWidth - rect.right };
        }
        this.group_menu_view = 'more';
        this.requestUpdate();
    }

    /**
     * For the "Назад" buttons inside the delete-confirm/disappearing-timer
     * sub-views -- unlike openMoreMenu(), does NOT recompute menu_anchor,
     * since that click originates from inside the already-open popup, not
     * from the original "Більше" button; recomputing from it would jump
     * the popup to wherever "Назад" happens to sit.
     * @param {MouseEvent} [ev]
     */
    backToMoreMenu(ev) {
        ev?.preventDefault?.();
        this.group_menu_view = 'more';
        this.requestUpdate();
    }

    /** @param {MouseEvent} [ev] */
    openDeleteConfirm(ev) {
        ev?.preventDefault?.();
        this.group_menu_view = 'delete-confirm';
        this.requestUpdate();
    }

    /** @param {MouseEvent} [ev] */
    openDisappearingTimer(ev) {
        ev?.preventDefault?.();
        this.group_menu_view = 'disappearing-timer';
        this.requestUpdate();
    }

    /** @param {MouseEvent} [ev] */
    closeGroupMenu(ev) {
        ev?.preventDefault?.();
        this.group_menu_view = null;
        this.menu_anchor = null;
        this.requestUpdate();
    }

    /**
     * For regular participants: leave the groupchat. Mirrors the "Leave"
     * handler in heading.js -- the group-left event must go out over the
     * wire before we close, since once we've left the room no longer
     * accepts anything from us to relay to the others.
     * @param {MouseEvent} [ev]
     */
    async leaveGroup(ev) {
        ev?.preventDefault?.();
        this.group_menu_view = null;
        this.menu_anchor = null;
        this.requestUpdate();
        const messages = [__('Ви впевнені, що хочете покинути цю групу?')];
        const result = await api.confirm(__('Підтвердження'), messages);
        if (!result) return;
        u.muc.sendGroupEvent(this.model, 'member-left');
        this.model.close();
    }

    /**
     * For owners/admins: destroy the groupchat entirely for everyone.
     * @param {MouseEvent} [ev]
     */
    async deleteGroup(ev) {
        ev?.preventDefault?.();
        this.group_menu_view = null;
        this.menu_anchor = null;
        this.requestUpdate();
        await destroyMUC(this.model);
    }

    /**
     * Both "Видалити для всіх" and "Видалити для мене" are local-only
     * clears -- neither retracts anything server-side, so they're
     * functionally identical (kept as two labeled options only to match the
     * requested UI; there's no permission distinction between them).
     * @param {MouseEvent} [ev]
     */
    startDeleteForMe(ev) {
        ev?.preventDefault?.();
        this.group_menu_view = null;
        this._scheduleDelete('me');
    }

    /** @param {MouseEvent} [ev] */
    startDeleteForEveryone(ev) {
        ev?.preventDefault?.();
        this.group_menu_view = null;
        this._scheduleDelete('everyone');
    }

    /**
     * @param {'me'|'everyone'} kind
     */
    _scheduleDelete(kind) {
        if (this.pending_delete) {
            clearTimeout(this.pending_delete.timeout_id);
        }
        const count = this.model.getRetractableMessages().length;
        const timeout_id = setTimeout(() => {
            this.pending_delete = null;
            this.requestUpdate();
            this.model.clearMessages();
        }, DELETE_UNDO_GRACE_MS);
        this.pending_delete = { kind, count, timeout_id };
        this.requestUpdate();
    }

    /** @param {MouseEvent} [ev] */
    undoDelete(ev) {
        ev?.preventDefault?.();
        if (!this.pending_delete) return;
        clearTimeout(this.pending_delete.timeout_id);
        this.pending_delete = null;
        this.requestUpdate();
    }

    /**
     * Purely local, per-device preference -- not synced to other members or
     * this account's other devices via any server-side mechanism. Only
     * affects which of THIS client's own live-delivered messages (see
     * headless/plugins/muc/parsers.js) get a self-destruct timer from now on.
     * @param {number} seconds - 0 disables disappearing messages
     */
    setDisappearingMessages(seconds) {
        this.group_menu_view = null;
        console.log(`[disappearing] setting disappearing_messages_ttl=${seconds > 0 ? seconds : null} for ${this.model.get('jid')}`);
        this.model.save('disappearing_messages_ttl', seconds > 0 ? seconds : null);
    }

    /** @param {MouseEvent} [ev] */
    async setCustomDisappearingMessages(ev) {
        ev?.preventDefault?.();
        const value = await api.prompt(
            __('Свій період'),
            [__('Введіть, через скільки хвилин нові повідомлення видалятимуться автоматично')],
            __('Хвилини'),
        );
        if (value === false || value === null) return;
        const minutes = parseInt(value, 10);
        if (!minutes || minutes <= 0) return;
        this.setDisappearingMessages(minutes * 60);
    }
}

api.elements.define('converse-muc-occupants', MUCOccupants);
